import { useEffect, useState } from 'react';

/**
 * useScrollSpy Hook 配置选项
 */
interface UseScrollSpyOptions {
  /**
   * 要监听的区域 ID 列表
   */
  sectionIds: string[];
  
  /**
   * 偏移量（像素），用于调整触发位置
   * 例如：如果导航栏高度为 80px，可以设置 offset: 100
   * @default 100
   */
  offset?: number;
  
  /**
   * 节流延迟时间（毫秒）
   * @default 100
   */
  throttleMs?: number;
}

/**
 * 节流函数：限制函数执行频率
 * 
 * @param func - 要节流的函数
 * @param delay - 延迟时间（毫秒）
 * @returns 节流后的函数
 */
function throttle<T extends (...args: unknown[]) => void>(
  func: T,
  delay: number
): (...args: Parameters<T>) => void {
  let timeoutId: ReturnType<typeof setTimeout> | null = null;
  let lastExecutedTime = 0;

  return function (this: unknown, ...args: Parameters<T>) {
    const now = Date.now();
    const timeSinceLastExecution = now - lastExecutedTime;

    const execute = () => {
      lastExecutedTime = now;
      func.apply(this, args);
    };

    if (timeSinceLastExecution >= delay) {
      // 如果距离上次执行已经超过延迟时间，立即执行
      execute();
    } else {
      // 否则，清除之前的定时器并设置新的定时器
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      timeoutId = setTimeout(execute, delay - timeSinceLastExecution);
    }
  };
}

/**
 * 监听滚动事件，检测当前视口中的内容区域
 * 
 * @param options - 配置选项
 * @returns 当前激活的区域 ID
 * 
 * @example
 * ```tsx
 * const activeSection = useScrollSpy({
 *   sectionIds: ['hero', 'features', 'use-cases', 'screenshots', 'faq'],
 *   offset: 100,
 * });
 * 
 * return (
 *   <nav>
 *     <a href="#hero" className={activeSection === 'hero' ? 'active' : ''}>
 *       首页
 *     </a>
 *   </nav>
 * );
 * ```
 */
export function useScrollSpy(options: UseScrollSpyOptions): string {
  const { sectionIds, offset = 100, throttleMs = 100 } = options;
  const [activeSection, setActiveSection] = useState<string>(sectionIds[0] || '');

  useEffect(() => {
    /**
     * 检测当前视口中的区域
     */
    const detectActiveSection = () => {
      // 获取当前滚动位置
      const scrollPosition = window.scrollY + offset;

      // 遍历所有区域，找到当前可见的区域
      for (let i = sectionIds.length - 1; i >= 0; i--) {
        const sectionId = sectionIds[i];
        const element = document.getElementById(sectionId);

        if (element) {
          const { offsetTop } = element;
          
          // 如果滚动位置已经超过该区域的顶部，则该区域为激活区域
          if (scrollPosition >= offsetTop) {
            setActiveSection(sectionId);
            return;
          }
        }
      }

      // 如果没有找到匹配的区域，默认激活第一个区域
      if (sectionIds.length > 0) {
        setActiveSection(sectionIds[0]);
      }
    };

    // 创建节流后的滚动处理函数
    const throttledDetect = throttle(detectActiveSection, throttleMs);

    // 初始检测
    detectActiveSection();

    // 监听滚动事件
    window.addEventListener('scroll', throttledDetect, { passive: true });

    // 监听窗口大小变化（可能影响区域位置）
    window.addEventListener('resize', throttledDetect, { passive: true });

    // 清理函数：移除事件监听器
    return () => {
      window.removeEventListener('scroll', throttledDetect);
      window.removeEventListener('resize', throttledDetect);
    };
  }, [sectionIds, offset, throttleMs]);

  return activeSection;
}
